import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { SelectPagination, Text, TextField } from "@foris/avocado-suite";
import TermSelect from "@modules/vacancies/Groups/term-select";
import CampusSelect from "@modules/shared/selectors/Selectors/Campus";
import ModalitySelect from "@modules/shared/selectors/Selectors/Modality";
import ProgramSelect from "@modules/shared/selectors/Selectors/Program";
import type { IOption } from "@common/components/Select/Select";
import type { Campus, Population, Term } from "@models/ISchema";
import css from "./packageDetailsFields.module.scss";

export interface SelectedPopulationOption {
  index: number;
  value: string;
  level?: number;
  self: Population;
}

interface PackageDetailsFieldsProps {
  selectedCampus: string;
  selectedModality: string;
  selectedPopulation: SelectedPopulationOption | null;
  packageName: string;
  populationSelectKey: string;
  canSelectPopulation: boolean;
  defaultTerm?: Term | null;
  isTermDisabled?: boolean;
  defaultCampus?: Campus | null;
  isCampusDisabled?: boolean;
  loadPopulationOptions: (searchTerm: string) => Promise<{
    options?: SelectedPopulationOption[];
    hasMore?: boolean;
    additional?: { page: number };
  }>;
  onTermChange: (termId: string) => void;
  onCampusChange: (campusId: string) => void;
  onModalityChange: (modalityId: string) => void;
  onProgramChange: (programId: string) => void;
  onPopulationChange: (population: SelectedPopulationOption | null) => void;
  onPopulationMenuClose: () => void;
  onPackageNameChange: (packageName: string) => void;
}

const PopulationOptionLabel = ({ population }: { population: Population }) => {
  const { t } = useTranslation();
  if (!population) return null;

  return (
    <div className={css.packageDetailsFields_populationOption}>
      <Text type="sm">
        {t("selectors.population-selector.campus")}: <strong>{population.campus?.code}</strong> |{" "}
        {t("selectors.population-selector.program")}: <strong>{population.program?.code}</strong> |{" "}
        {t("selectors.population-selector.curriculum")}: <strong>{population.curriculum}</strong> |{" "}
        {t("selectors.population-selector.level")}: <strong>{population.level}</strong>
      </Text>
      <Text type="xs">
        {t("selectors.population-selector.school")}:{" "}
        <strong>{population.program?.department?.code}</strong> |{" "}
        {t("selectors.population-selector.modality")}: <strong>{population.modality?.code}</strong>{" "}
        | {t("selectors.population-selector.shift")}: <strong>{population.shift?.code}</strong>
      </Text>
    </div>
  );
};

const PackageDetailsFields: FC<PackageDetailsFieldsProps> = ({
  selectedCampus,
  selectedModality,
  selectedPopulation,
  packageName,
  populationSelectKey,
  canSelectPopulation,
  defaultTerm,
  isTermDisabled = false,
  defaultCampus,
  isCampusDisabled = false,
  loadPopulationOptions,
  onTermChange,
  onCampusChange,
  onModalityChange,
  onProgramChange,
  onPopulationChange,
  onPopulationMenuClose,
  onPackageNameChange,
}) => {
  const { t } = useTranslation();

  return (
    <div className={css.packageDetailsFields}>
      <TermSelect
        placeholder={t("create-package.forms.select-period.placeholder")}
        className={css.packageDetailsFields_field}
        sendValues={({ term }) => onTermChange(term ?? "")}
        defaultTerm={defaultTerm}
        isDisabled={isTermDisabled}
      />

      <CampusSelect
        label={t("create-package.forms.select-campus.label")}
        placeholder={t("create-package.forms.select-campus.placeholder")}
        className={css.packageDetailsFields_field}
        onCallback={(campus: IOption) => onCampusChange(campus?.value || "")}
        defaultCampus={defaultCampus}
        isDisabled={isCampusDisabled}
      />
      <ModalitySelect
        label={t("create-package.forms.select-modality.label")}
        placeholder={t("create-package.forms.select-modality.placeholder")}
        className={css.packageDetailsFields_field}
        shouldHasDepartment={false}
        onCallback={(modality: IOption) => onModalityChange(modality?.value || "")}
      />
      <ProgramSelect
        className={css.packageDetailsFields_field}
        label={t("create-package.forms.select-career.label")}
        placeholder={t("create-package.forms.select-career.placeholder")}
        campus={selectedCampus}
        modality={selectedModality}
        shouldHaveFilters={false}
        filterBy="modality"
        onCallback={(program: IOption) => onProgramChange(program?.value || "")}
      />

      <SelectPagination
        key={populationSelectKey}
        className={css.packageDetailsFields_field}
        disabled={!canSelectPopulation}
        value={selectedPopulation}
        formatOptionLabel={(option: SelectedPopulationOption) => (
          <PopulationOptionLabel population={option?.self} />
        )}
        debounceTimeout={500}
        label={t("create-package.forms.select-population.label")}
        placeholder={t("create-package.forms.select-population.placeholder")}
        onChange={onPopulationChange}
        loadOptions={loadPopulationOptions}
        onMenuClose={onPopulationMenuClose}
      />
      <TextField
        className={css.packageDetailsFields_numerator}
        disabled
        label={t("create-package.forms.input-numerator.label")}
        placeholder={t("create-package.forms.input-numerator.placeholder")}
        type="number"
        value={selectedPopulation ? selectedPopulation.index : ""}
      />
      <TextField
        className={css.packageDetailsFields_field}
        disabled={!selectedPopulation?.index}
        label={t("create-package.forms.input-package.label")}
        placeholder={t("create-package.forms.input-package.placeholder")}
        type="text"
        value={packageName}
        onChange={event => onPackageNameChange(event.target.value)}
      />
    </div>
  );
};

export default PackageDetailsFields;
